export function pickRecordingMimeType() {
  if (typeof window === "undefined" || typeof window.MediaRecorder === "undefined") {
    return "audio/webm";
  }

  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
    "audio/ogg",
    "audio/mp4",
  ];

  const supported = candidates.find((type) => window.MediaRecorder.isTypeSupported?.(type));
  return supported || "audio/webm";
}

export function fileExtensionFromMimeType(mimeType) {
  const normalized = String(mimeType || "").toLowerCase();
  if (normalized.includes("wav")) return ".wav";
  if (normalized.includes("ogg")) return ".ogg";
  if (normalized.includes("mp4") || normalized.includes("m4a")) return ".m4a";
  if (normalized.includes("mpeg") || normalized.includes("mp3")) return ".mp3";
  return ".webm";
}

export async function requestAudioStream(constraints = { audio: true }) {
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
    const error = new Error("microphone-unsupported");
    error.code = "microphone-unsupported";
    throw error;
  }

  return navigator.mediaDevices.getUserMedia(constraints);
}

export function stopMediaStream(stream) {
  if (!stream) return;
  stream.getTracks().forEach((track) => track.stop());
}

export function createAudioLevelMonitor(stream, onLevel) {
  if (typeof window === "undefined" || !stream) {
    return () => {};
  }

  const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextCtor) {
    return () => onLevel?.(0);
  }

  let frameId = null;
  let source = null;
  let audioContext = null;
  let analyser = null;

  try {
    audioContext = new AudioContextCtor();
    analyser = audioContext.createAnalyser();
    source = audioContext.createMediaStreamSource(stream);
    const data = new Uint8Array(analyser.frequencyBinCount);

    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.85;
    source.connect(analyser);

    const updateLevel = () => {
      if (!analyser) return;
      analyser.getByteFrequencyData(data);
      const average =
        data.reduce((sum, value) => sum + value, 0) /
        Math.max(data.length, 1) /
        255;
      onLevel?.(Number(average.toFixed(3)));
      frameId = window.requestAnimationFrame(updateLevel);
    };

    frameId = window.requestAnimationFrame(updateLevel);
  } catch (_error) {
    onLevel?.(0);
  }

  return () => {
    if (frameId) window.cancelAnimationFrame(frameId);
    frameId = null;

    try {
      source?.disconnect();
    } catch (_error) {}

    source = null;
    analyser = null;

    if (audioContext) {
      audioContext.close().catch(() => {});
    }

    audioContext = null;
    onLevel?.(0);
  };
}

export function createAudioElementLevelMonitor(element, onLevel) {
  if (typeof window === "undefined" || !element) {
    return () => {};
  }

  const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextCtor) {
    return () => onLevel?.(0);
  }

  let frameId = null;
  let audioContext = null;
  let source = null;
  let analyser = null;

  try {
    audioContext = new AudioContextCtor();
    analyser = audioContext.createAnalyser();
    source = audioContext.createMediaElementSource(element);
    const data = new Uint8Array(analyser.frequencyBinCount);

    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.82;
    source.connect(analyser);
    analyser.connect(audioContext.destination);

    const updateLevel = () => {
      if (!analyser) return;
      analyser.getByteFrequencyData(data);
      const average =
        data.reduce((sum, value) => sum + value, 0) /
        Math.max(data.length, 1) /
        255;
      onLevel?.(Number(average.toFixed(3)));
      frameId = window.requestAnimationFrame(updateLevel);
    };

    const ensureRunning = () => audioContext?.resume?.().catch(() => {});
    element.addEventListener("play", ensureRunning);
    frameId = window.requestAnimationFrame(updateLevel);

    return () => {
      element.removeEventListener("play", ensureRunning);
      if (frameId) window.cancelAnimationFrame(frameId);
      frameId = null;

      try {
        source?.disconnect();
      } catch (_error) {}

      try {
        analyser?.disconnect();
      } catch (_error) {}

      source = null;
      analyser = null;
      if (audioContext) {
        audioContext.close().catch(() => {});
      }
      audioContext = null;
      onLevel?.(0);
    };
  } catch (_error) {
    onLevel?.(0);
    return () => onLevel?.(0);
  }
}

export function createAnalyserLevelMonitor(analyser, onLevel) {
  if (typeof window === "undefined" || !analyser) {
    return () => {};
  }

  const data = new Uint8Array(analyser.frequencyBinCount);
  let frameId = null;

  const updateLevel = () => {
    analyser.getByteFrequencyData(data);
    const average =
      data.reduce((sum, value) => sum + value, 0) /
      Math.max(data.length, 1) /
      255;
    onLevel?.(Number(average.toFixed(3)));
    frameId = window.requestAnimationFrame(updateLevel);
  };

  frameId = window.requestAnimationFrame(updateLevel);

  return () => {
    if (frameId) window.cancelAnimationFrame(frameId);
    frameId = null;
    onLevel?.(0);
  };
}
